import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuthState } from '@/hooks/useAuthState';
import AdminLayout from '@/layouts/AdminLayout';
import { ArrowLeft, Save, Building2, Mail, Phone, Globe, MapPin, Upload, Camera, X, Loader2, Clock3, Images, Sparkles, Plus, Trash2 } from 'lucide-react';
import {
  DevelopmentService,
  Development,
  DevelopmentAmenity,
  parseDevelopmentAmenities,
} from '@/services/developmentService';
import {
  AMENITY_ICON_KEYS,
  AMENITY_ICONS,
  DEFAULT_AMENITY_ICON_KEY,
  resolveAmenityIcon,
  type AmenityIconKey,
} from '@/lib/amenityIcons';
import { getApiEndpoint, getDevelopmentLogoUrl } from '@/config/api';
import RichTextEditor from '@/components/RichTextEditor';
import { CountrySelect, Country } from '@/components/CountrySelect';
import InputMask from 'react-input-mask';

// Estados brasileiros (siglas)
const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const AdminEditDevelopment = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { isAuthenticated, userType, loading: authLoading } = useAuthState();
  
  // Determinar se é criação ou edição
  const isCreating = !id || id === 'novo';
  
  // Função para aplicar máscara de telefone brasileiro
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };
  
  const [developmentData, setDevelopmentData] = useState<Development | null>(null);
  const [loading, setLoading] = useState(!isCreating);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [pendingPhotoFiles, setPendingPhotoFiles] = useState<Array<{ tempId: string; file: File; previewUrl: string }>>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    link_interno: '',
    description: '',
    address: '',
    city: '',
    state: '',
    website: '',
    email: '',
    phone: '',
    logo: '',
    check_in_time: '15:00',
    check_out_time: '11:00',
    house_rules: '',
    additional_info: '',
    photos: [] as string[],
    cover_image: '',
    amenities: [] as DevelopmentAmenity[],
    is_active: true
  });
  const [phoneDdi, setPhoneDdi] = useState('55'); // Brasil como padrão
  const [emailError, setEmailError] = useState<string>('');

  // Verificar autenticação (AdminLayout já faz isso)

  // Carregar dados do empreendimento se estiver editando
  useEffect(() => {
    if (!isCreating && id) {
      loadDevelopment();
    } else {
      setLoading(false);
    }
  }, [id, isCreating]);

  const loadDevelopment = async () => {
    try {
      setLoading(true);
      const development = await DevelopmentService.getDevelopmentById(parseInt(id!));
      if (development) {
        setDevelopmentData(development);
        let parsedPhotos: string[] = [];
        try {
          parsedPhotos = Array.isArray((development as any).photos)
            ? (development as any).photos
            : typeof (development as any).photos === 'string' && (development as any).photos.trim() !== ''
              ? JSON.parse((development as any).photos)
              : [];
        } catch {
          parsedPhotos = [];
        }
        setFormData({
          name: development.name || '',
          link_interno: development.link_interno || '',
          description: development.description || '',
          address: development.address || '',
          city: development.city || '',
          state: development.state || '',
          website: development.website || '',
          email: development.email || '',
          phone: development.phone || '',
          logo: development.logo || '',
          check_in_time: (development as any).check_in_time || '15:00',
          check_out_time: (development as any).check_out_time || '11:00',
          house_rules: (development as any).house_rules || '',
          additional_info: (development as any).additional_info || '',
          photos: parsedPhotos,
          cover_image: (development as any).cover_image || '',
          amenities: parseDevelopmentAmenities((development as any).amenities),
          is_active: development.is_active !== undefined ? development.is_active : true
        });
        // Carregar DDI do empreendimento ou usar padrão
        setPhoneDdi((development as any).phone_ddi || '55');
        
        // Se houver logo, criar preview
        if (development.logo) {
          const logoUrl = getDevelopmentLogoUrl(development.logo);
          setLogoPreview(logoUrl);
          console.log('📸 Logo carregada:', { logo: development.logo, logoUrl });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empreendimento:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados do empreendimento',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDevelopmentPhotosUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const validFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (validFiles.length === 0) return;

    if (isCreating || !id) {
      const newPending = validFiles.map((file, index) => ({
        tempId: `temp-${Date.now()}-${index}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      setPendingPhotoFiles((prev) => [...prev, ...newPending]);
      setFormData((prev) => ({
        ...prev,
        cover_image: prev.cover_image || newPending[0]?.tempId || '',
      }));
      event.target.value = '';
      return;
    }

    try {
      setUploadingPhotos(true);
      const token = localStorage.getItem('authToken');
      const headers: HeadersInit = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const uploadedPaths: string[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('developmentId', id);
        const response = await fetch(getApiEndpoint('api/upload/development-logo'), {
          method: 'POST',
          headers,
          body: uploadFormData,
        });
        if (!response.ok) continue;
        const result = await response.json();
        uploadedPaths.push(result.path || `/empreendimentos/${result.filename}`);
      }

      if (uploadedPaths.length > 0) {
        const merged = [...(formData.photos || []), ...uploadedPaths];
        const nextCover = formData.cover_image || uploadedPaths[0];
        await DevelopmentService.updateDevelopment(parseInt(id), {
          photos: merged,
          cover_image: nextCover,
        });
        setFormData((prev) => ({ ...prev, photos: merged, cover_image: nextCover }));
      }
    } finally {
      setUploadingPhotos(false);
      event.target.value = '';
    }
  };

  const removeDevelopmentPhoto = async (photoPath: string) => {
    const nextPhotos = (formData.photos || []).filter((p) => p !== photoPath);
    const nextCover = formData.cover_image === photoPath ? (nextPhotos[0] || '') : formData.cover_image;
    if (isCreating || !id) {
      setFormData((prev) => ({
        ...prev,
        photos: nextPhotos,
        cover_image: nextCover,
      }));
      return;
    }
    await DevelopmentService.updateDevelopment(parseInt(id), {
      photos: nextPhotos,
      cover_image: nextCover || null,
    });
    setFormData((prev) => ({ ...prev, photos: nextPhotos, cover_image: nextCover }));
  };

  const removePendingPhoto = (tempId: string) => {
    setPendingPhotoFiles((prev) => {
      const target = prev.find((p) => p.tempId === tempId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      const next = prev.filter((p) => p.tempId !== tempId);
      if (formData.cover_image === tempId) {
        setFormData((current) => ({ ...current, cover_image: next[0]?.tempId || current.photos[0] || '' }));
      }
      return next;
    });
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field === 'phone') {
      value = formatPhoneNumber(value as string);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validar email
    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value as string)) {
        setEmailError('Email inválido');
      } else {
        setEmailError('');
      }
    }
  };

  // ==== Amenidades do empreendimento ====
  const addAmenity = () => {
    setFormData((prev) => ({
      ...prev,
      amenities: [
        ...prev.amenities,
        { name: '', description: '', icon: DEFAULT_AMENITY_ICON_KEY },
      ],
    }));
  };

  const updateAmenity = (
    index: number,
    field: keyof DevelopmentAmenity,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.map((amenity, i) =>
        i === index ? { ...amenity, [field]: value } : amenity,
      ),
    }));
  };

  const removeAmenity = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index),
    }));
  };

  // Mantém apenas amenidades com `name` preenchido (descrição/ícone opcionais)
  // antes de enviar ao backend.
  const sanitizeAmenities = (
    amenities: DevelopmentAmenity[],
  ): DevelopmentAmenity[] =>
    amenities
      .map((a) => ({
        name: a.name.trim(),
        description: a.description?.trim() ? a.description.trim() : undefined,
        icon: a.icon || undefined,
      }))
      .filter((a) => a.name.length > 0);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione apenas arquivos de imagem.',
        variant: 'destructive',
      });
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Erro',
        description: 'A imagem deve ter no máximo 5MB.',
        variant: 'destructive',
      });
      return;
    }

    // Para modo de criação, apenas armazenar o arquivo
    if (isCreating) {
      setLogoFile(file);
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
      console.log('📁 Arquivo selecionado para upload posterior:', file.name);
      return;
    }

    // Para modo de edição, fazer upload imediatamente
    if (!id) {
      console.log('❌ ID não encontrado para edição');
      return;
    }

    try {
      setUploadingLogo(true);
      console.log('📤 Fazendo upload da logo:', file.name, 'para empreendimento ID:', id);
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('developmentId', id);
      
      const token = localStorage.getItem('authToken');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiEndpoint('api/upload/development-logo'), {
        method: 'POST',
        headers,
        body: uploadFormData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro no upload');
      }
      
      const result = await response.json();
      console.log('✅ Upload realizado:', result);
      
      // Garantir que o caminho está correto
      const logoPath = result.path || `/empreendimentos/${result.filename}`;
      console.log('📸 Caminho da logo a ser salvo:', logoPath);
      
      // Atualizar o empreendimento com a nova logo
      await DevelopmentService.updateDevelopment(parseInt(id), {
        logo: logoPath
      });
      
      // Atualizar o estado local
      setFormData(prev => ({ ...prev, logo: logoPath }));
      setLogoPreview(getDevelopmentLogoUrl(logoPath));
      setLogoFile(null);
      
      toast({
        title: 'Sucesso',
        description: 'Logo enviada com sucesso!',
      });
    } catch (error: any) {
      console.error('❌ Erro ao fazer upload da logo:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível fazer upload da logo',
        variant: 'destructive',
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoFile(null);
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
      setLogoPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!isCreating && id) {
      try {
        // Se estiver editando, remover logo do banco
        await DevelopmentService.updateDevelopment(parseInt(id), { logo: null });
        setFormData(prev => ({ ...prev, logo: '' }));
        toast({
          title: 'Sucesso',
          description: 'Logo removida com sucesso!',
        });
      } catch (error) {
        console.error('Erro ao remover logo:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível remover a logo',
          variant: 'destructive',
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (emailError) {
      toast({
        title: 'Erro de validação',
        description: 'Por favor, corrija os erros no formulário',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      
      if (isCreating) {
        // Criar empreendimento
        const createData = {
          ...formData,
          photos: [],
          cover_image: null,
          amenities: sanitizeAmenities(formData.amenities),
          phone_ddi: phoneDdi
        };
        const newDevelopment = await DevelopmentService.createDevelopment(createData);
        
        // Se houver logo para upload, fazer upload após criar
        if (logoFile && newDevelopment.id) {
          try {
            setUploadingLogo(true);
            const uploadFormData = new FormData();
            uploadFormData.append('file', logoFile);
            uploadFormData.append('developmentId', newDevelopment.id.toString());
            
            const token = localStorage.getItem('authToken');
            const headers: HeadersInit = {};
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(getApiEndpoint('api/upload/development-logo'), {
              method: 'POST',
              headers,
              body: uploadFormData,
            });
            
            if (response.ok) {
              const result = await response.json();
              // Garantir que o caminho está correto
              const logoPath = result.path || `/empreendimentos/${result.filename}`;
              console.log('📸 Caminho da logo a ser salvo:', logoPath);
              // Atualizar empreendimento com a logo
              await DevelopmentService.updateDevelopment(newDevelopment.id, {
                logo: logoPath
              });
              // Atualizar o estado local
              setFormData(prev => ({ ...prev, logo: logoPath }));
              setLogoPreview(getDevelopmentLogoUrl(logoPath));
            }
          } catch (uploadError) {
            console.error('Erro ao fazer upload da logo:', uploadError);
            // Não falha a criação se o upload falhar
          } finally {
            setUploadingLogo(false);
          }
        }

        if (pendingPhotoFiles.length > 0 && newDevelopment.id) {
          const token = localStorage.getItem('authToken');
          const headers: HeadersInit = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const uploadedPaths: string[] = [];
          const coverTempId = formData.cover_image.startsWith('temp-') ? formData.cover_image : '';
          let resolvedCoverImage = '';

          for (const pending of pendingPhotoFiles) {
            const uploadFormData = new FormData();
            uploadFormData.append('file', pending.file);
            uploadFormData.append('developmentId', newDevelopment.id.toString());

            const response = await fetch(getApiEndpoint('api/upload/development-logo'), {
              method: 'POST',
              headers,
              body: uploadFormData,
            });
            if (!response.ok) continue;
            const result = await response.json();
            const photoPath = result.path || `/empreendimentos/${result.filename}`;
            uploadedPaths.push(photoPath);
            if (pending.tempId === coverTempId) resolvedCoverImage = photoPath;
          }

          if (uploadedPaths.length > 0) {
            await DevelopmentService.updateDevelopment(newDevelopment.id, {
              photos: uploadedPaths,
              cover_image: resolvedCoverImage || uploadedPaths[0],
            });
          }
        }
        
        toast({
          title: 'Sucesso',
          description: 'Empreendimento criado com sucesso',
        });
      } else {
        let nextPhotos = [...(formData.photos || [])];
        let nextCoverImage = formData.cover_image;

        if (pendingPhotoFiles.length > 0) {
          const token = localStorage.getItem('authToken');
          const headers: HeadersInit = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const coverTempId = formData.cover_image.startsWith('temp-') ? formData.cover_image : '';

          for (const pending of pendingPhotoFiles) {
            const uploadFormData = new FormData();
            uploadFormData.append('file', pending.file);
            uploadFormData.append('developmentId', id!);
            const response = await fetch(getApiEndpoint('api/upload/development-logo'), {
              method: 'POST',
              headers,
              body: uploadFormData,
            });
            if (!response.ok) continue;
            const result = await response.json();
            const photoPath = result.path || `/empreendimentos/${result.filename}`;
            nextPhotos.push(photoPath);
            if (pending.tempId === coverTempId) nextCoverImage = photoPath;
          }
        }

        const updateData = {
          ...formData,
          photos: nextPhotos,
          cover_image: nextCoverImage || null,
          amenities: sanitizeAmenities(formData.amenities),
          phone_ddi: phoneDdi
        };
        await DevelopmentService.updateDevelopment(parseInt(id!), updateData);
        toast({
          title: 'Sucesso',
          description: 'Empreendimento atualizado com sucesso',
        });
      }
      
      navigate('/admin/empreendimentos');
    } catch (error: any) {
      console.error('Erro ao salvar empreendimento:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao salvar empreendimento',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      pendingPhotoFiles.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
  }, [pendingPhotoFiles]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/empreendimentos')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isCreating ? 'Novo Empreendimento' : 'Editar Empreendimento'}
              </h1>
              <p className="text-muted-foreground">
                {isCreating 
                  ? 'Preencha as informações do novo empreendimento'
                  : 'Atualize as informações do empreendimento'}
              </p>
            </div>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informações do Empreendimento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Nome */}
                <div>
                  <Label htmlFor="name">
                    Nome do Empreendimento <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ex: Porto 2 Life"
                    required
                  />
                </div>

                {/* Link Interno */}
                <div>
                  <Label htmlFor="link_interno">Link Interno</Label>
                  <Input
                    id="link_interno"
                    value={formData.link_interno}
                    onChange={(e) =>
                      handleInputChange(
                        'link_interno',
                        e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, ''),
                      )
                    }
                    placeholder="ex: porto-2-life"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    URL final no site: {window.location.origin}/resorts/
                    {formData.link_interno || 'seu-link'}
                  </p>
                </div>

                {/* Descrição */}
                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => handleInputChange('description', value)}
                    placeholder="Descreva o empreendimento..."
                    minHeight="200px"
                  />
                </div>

                {/* Horários padrão */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="check_in_time">Check-in padrão</Label>
                    <Input
                      id="check_in_time"
                      type="time"
                      value={formData.check_in_time}
                      onChange={(e) => handleInputChange('check_in_time', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="check_out_time">Check-out padrão</Label>
                    <Input
                      id="check_out_time"
                      type="time"
                      value={formData.check_out_time}
                      onChange={(e) => handleInputChange('check_out_time', e.target.value)}
                    />
                  </div>
                </div>

                {/* Endereço */}
                <div>
                  <Label htmlFor="address">Endereço</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Ex: Muro Alto, Porto de Galinhas"
                  />
                </div>

                {/* Cidade e Estado */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Ex: Porto de Galinhas"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">Estado</Label>
                    <Select
                      value={formData.state || ''}
                      onValueChange={(value) => handleInputChange('state', value)}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder="Selecione o estado" />
                      </SelectTrigger>
                      <SelectContent>
                        {BRAZILIAN_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="contato@empreendimento.com"
                  />
                  {emailError && (
                    <p className="text-sm text-destructive mt-1">{emailError}</p>
                  )}
                </div>

                {/* Telefone */}
                <div>
                  <Label htmlFor="phone">Telefone</Label>
                  <div className="flex gap-2">
                    <CountrySelect
                      value={phoneDdi}
                      onValueChange={(ddi: string, country: Country) => {
                        setPhoneDdi(ddi);
                      }}
                      disabled={saving}
                    />
                    <InputMask
                      mask="(99) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', formatPhoneNumber(e.target.value))}
                      disabled={saving}
                      className="flex-1"
                    >
                      {(inputProps: any) => (
                        <Input
                          {...inputProps}
                          id="phone"
                          placeholder="(81) 99999-9999"
                          maxLength={15}
                          className="flex-1"
                        />
                      )}
                    </InputMask>
                  </div>
                </div>

                {/* Website */}
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://www.empreendimento.com"
                  />
                </div>

                {/* Logo Upload */}
                <div>
                  <Label>Logo do Empreendimento</Label>
                  <div className="mt-2 space-y-4">
                    {/* Preview da logo atual ou selecionada */}
                    {(logoPreview || formData.logo) && (
                      <div className="relative inline-block">
                        <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                          <img
                            src={logoPreview || getDevelopmentLogoUrl(formData.logo)}
                            alt="Logo do empreendimento"
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder.svg';
                            }}
                          />
                        </div>
                        {(logoPreview || formData.logo) && (
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                            onClick={handleRemoveLogo}
                            disabled={uploadingLogo}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Input de arquivo */}
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo || saving}
                      >
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            {logoPreview || formData.logo ? 'Trocar Logo' : 'Selecionar Logo'}
                          </>
                        )}
                      </Button>
                      
                      {logoFile && isCreating && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB) - será enviado ao salvar
                        </p>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Fotos padrão do empreendimento</Label>
                  <div className="space-y-3 mt-2">
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleDevelopmentPhotosUpload}
                        className="hidden"
                        id="development-photos-upload"
                        disabled={uploadingPhotos}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingPhotos}
                        onClick={() => document.getElementById('development-photos-upload')?.click()}
                      >
                        <Images className="h-4 w-4 mr-2" />
                        {uploadingPhotos ? 'Enviando fotos...' : 'Adicionar fotos'}
                      </Button>
                      {isCreating && (
                        <p className="text-xs text-muted-foreground mt-2">
                          As fotos serão enviadas ao salvar o empreendimento.
                        </p>
                      )}
                    </div>

                    {(formData.photos || []).length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(formData.photos || []).map((photoPath) => (
                          <div key={photoPath} className="relative rounded-lg overflow-hidden border">
                            <img
                              src={getDevelopmentLogoUrl(photoPath)}
                              alt="Foto do empreendimento"
                              className="w-full h-24 object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeDevelopmentPhoto(photoPath)}
                              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData((prev) => ({ ...prev, cover_image: photoPath }))}
                              className={`absolute bottom-1 left-1 rounded px-2 py-1 text-[10px] font-semibold ${
                                formData.cover_image === photoPath ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white'
                              }`}
                            >
                              {formData.cover_image === photoPath ? 'Capa' : 'Definir capa'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {pendingPhotoFiles.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {pendingPhotoFiles.map((pending) => (
                          <div key={pending.tempId} className="relative rounded-lg overflow-hidden border border-dashed">
                            <img
                              src={pending.previewUrl}
                              alt="Foto pendente"
                              className="w-full h-24 object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removePendingPhoto(pending.tempId)}
                              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData((prev) => ({ ...prev, cover_image: pending.tempId }))}
                              className={`absolute bottom-1 left-1 rounded px-2 py-1 text-[10px] font-semibold ${
                                formData.cover_image === pending.tempId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white'
                              }`}
                            >
                              {formData.cover_image === pending.tempId ? 'Capa' : 'Definir capa'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="house_rules">Regras da casa (padrão do empreendimento)</Label>
                  <RichTextEditor
                    value={formData.house_rules}
                    onChange={(value) => handleInputChange('house_rules', value)}
                    placeholder="Regras padrão aplicadas aos imóveis deste empreendimento..."
                    minHeight="180px"
                  />
                </div>

                <div>
                  <Label htmlFor="additional_info">Informações adicionais (padrão do empreendimento)</Label>
                  <RichTextEditor
                    value={formData.additional_info}
                    onChange={(value) => handleInputChange('additional_info', value)}
                    placeholder="Informações extras padrão para todos os imóveis deste empreendimento..."
                    minHeight="180px"
                  />
                </div>

                {/* Amenidades */}
                <div className="rounded-lg border border-border bg-muted/30 p-4 sm:p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <Label className="text-base font-semibold">Amenidades</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Cada amenidade tem um nome e uma descrição opcional. Ex.: <em>Áreas de lazer</em> — <em>Piscinas, áreas de descanso e ambientes pensados para relaxar e curtir.</em>
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addAmenity}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar amenidade
                    </Button>
                  </div>

                  {formData.amenities.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border bg-background/60 px-4 py-6 text-center text-sm text-muted-foreground">
                      Nenhuma amenidade cadastrada. Clique em <span className="font-medium">Adicionar amenidade</span> para começar.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.amenities.map((amenity, index) => {
                        const PreviewIcon = resolveAmenityIcon(amenity.icon).Icon;
                        return (
                          <div
                            key={index}
                            className="relative rounded-lg border border-border bg-background p-4 pr-12 shadow-sm"
                          >
                            <button
                              type="button"
                              onClick={() => removeAmenity(index)}
                              className="absolute top-2 right-2 size-8 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive grid place-items-center transition-colors"
                              aria-label={`Remover amenidade ${index + 1}`}
                              title="Remover amenidade"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>

                            <div className="flex gap-3 items-start">
                              {/* Preview do ícone */}
                              <div className="size-12 shrink-0 rounded-xl bg-gradient-to-br from-primary to-secondary grid place-items-center shadow-md">
                                <PreviewIcon className="size-6 text-primary-foreground" />
                              </div>

                              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <Label htmlFor={`amenity-icon-${index}`} className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Ícone
                                  </Label>
                                  <Select
                                    value={amenity.icon || DEFAULT_AMENITY_ICON_KEY}
                                    onValueChange={(value) =>
                                      updateAmenity(index, 'icon', value as AmenityIconKey)
                                    }
                                  >
                                    <SelectTrigger id={`amenity-icon-${index}`} className="mt-1">
                                      <SelectValue placeholder="Selecionar ícone" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {AMENITY_ICON_KEYS.map((key) => {
                                        const { Icon, label } = AMENITY_ICONS[key];
                                        return (
                                          <SelectItem key={key} value={key}>
                                            <span className="inline-flex items-center gap-2">
                                              <Icon className="size-4 text-primary" />
                                              <span>{label}</span>
                                            </span>
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label htmlFor={`amenity-name-${index}`} className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Amenidade <span className="text-destructive">*</span>
                                  </Label>
                                  <Input
                                    id={`amenity-name-${index}`}
                                    value={amenity.name}
                                    onChange={(e) => updateAmenity(index, 'name', e.target.value)}
                                    placeholder="Ex: Áreas de lazer"
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`amenity-desc-${index}`} className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Descrição
                                  </Label>
                                  <Textarea
                                    id={`amenity-desc-${index}`}
                                    value={amenity.description ?? ''}
                                    onChange={(e) => updateAmenity(index, 'description', e.target.value)}
                                    placeholder="Ex: Piscinas, áreas de descanso e ambientes pensados para relaxar e curtir."
                                    rows={2}
                                    className="mt-1 resize-y"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="is_active">Status</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.is_active ? 'Empreendimento ativo' : 'Empreendimento inativo'}
                    </p>
                  </div>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                  />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/minha-area/admin/empreendimentos')}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Salvando...' : isCreating ? 'Criar Empreendimento' : 'Salvar Alterações'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEditDevelopment;

